<!-- Copyright: Seán I. O'Donoghue -->
<!-- TODO: Enable routes for Videos/2023 and Videos/2023#Cells -->

<template>
  <!-- <p>Gallery kinda here</p> -->
  <div v-if="options && options.showcases">
    <!-- eslint-disable-next-line vue/no-template-shadow -->
    <section
      v-for="(showcase, index) in sortByYear(options.filteredShowcases)"
      :key="showcase"
      :ref="observeShowcase"
      :data-showcase="showcase">
      <h4>{{ showcase }}</h4>
      <div class="showcaseVideos" :aria-busy="!getIsShown(showcase, index)">
        <template v-if="getIsShown(showcase, index)">
          <GalleryItem
            v-for="vimeoHash in getHashes(showcase)"
            :key="vimeoHash"
            :options="options"
            :showcase="showcase"
            :vimeo-hash="vimeoHash"
            @filterByYear="filterByYear"
            @filterBySession="filterBySession" />
        </template>
        <!-- placeholders, as many as the showcase's videos, until it comes near the screen -->
        <div v-for="slot in getHashes(showcase).length" v-else :key="slot" class="placeholder" />
      </div>
    </section>
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line
// eslint-disable-next-line
const props = defineProps({options: {default: {}}});
import {onBeforeUnmount, reactive} from "vue";
import {useVizbiOrder} from "@/composables/useVizbiOrder";
import GalleryItem from "@/components/Video/GalleryItem";
const emit = defineEmits(["filterByYear", "filterBySession"]);

// long pages (DESIGN.md § Long pages, as /Photos): every showcase laid out at once, placeholders
// in its videos' places; the newest two shown straight away, each further one as it comes near
// the screen. Showing every video at once had the browser fetch some 300 thumbnails, which on a
// phone held up the access check of the video clicked: 20–30 s for its Purchase access
// (2026-10-18)
const FIRST_SHOWCASES = 2;
const AHEAD = "1500px 0px"; // show a showcase this far before it's scrolled into view
const nearShowcases = reactive(new Set());

const getHashes = (showcase) => useVizbiOrder(props.options.filteredShowcases[showcase]["videos"]);
const getIsShown = (showcase, index) => index < FIRST_SHOWCASES || nearShowcases.has(showcase);

function onNear(entries) {
  for (const entry of entries.filter((item) => item.isIntersecting)) {
    nearShowcases.add(entry.target.dataset.showcase);
  }
}

const observer = new IntersectionObserver(onNear, {rootMargin: AHEAD});

function observeShowcase(element) {
  if (element) observer.observe(element);
}

onBeforeUnmount(() => observer.disconnect());
function sortByYear(filteredShowcases) {
  let showcaseNames = Object.keys(filteredShowcases);
  return showcaseNames.sort((a, b) => {
    // remove everything that's not a number, then sort
    // https://stackoverflow.com/q/10003683
    return b.replace(/^\D+/g, "") - a.replace(/^\D+/g, "");
  });
}
function filterByYear(year) {
  emit("filterByYear", year);
}
function filterBySession(session) {
  emit("filterBySession", session);
}
</script>

<style scoped>
/* a placeholder takes a video's place in the grid (GalleryItem.vue): its thumbnail and caption,
   grey until the showcase comes near the screen */
.placeholder {
  /* without it, the aspect ratio gave the grid column a minimum width from the height: wider than
     a phone (633px on an iPhone, 2026-10-21) */
  min-width: 0;
  aspect-ratio: 16 / 9;
  margin-bottom: 4.5em;
  border-radius: 4px;
  background: #eee; /* as /Photos' placeholders */
}
section div.showcaseVideos {
  /* mobile first */
  display: grid;
  gap: 16px 8px; /* Y X */
  grid-template-columns: repeat(2, 1fr); /* Creates three equal columns */
}
.medium .showcaseVideos {
  grid-template-columns: repeat(2, 1fr); /* Creates two equal columns */
}
.unlimited .showcaseVideos,
.wide .showcaseVideos {
  grid-template-columns: repeat(3, 1fr); /* Creates three equal columns */
}
</style>
